import {useEffect, useMemo, useState} from "react";
import axios from "axios";
import Block from "../block";
import Spinner from "../../components/spinner";

export default function AccountUnsubscribe(props) {
    const token = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("unsubscribe_token");
    }, []);

    const [state, setState] = useState("loading");
    const [values, setValues] = useState({
        optInEmail: false,
        optInSms: false,
        emailHint: "",
        displayHint: ""
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        document.title = props?.title || "Email Preferences";

        if (!token) {
            setState("missing-token");
            return;
        }

        axios.get(`/api/v2/publicusers/preferences?unsubscribe_token=${encodeURIComponent(token)}`)
            .then(({data}) => {
                setValues({
                    optInEmail: data?.optInEmail ?? false,
                    optInSms: data?.optInSms ?? false,
                    emailHint: data?.emailHint ?? "",
                    displayHint: data?.displayHint ?? ""
                });
                setState("ready");
            })
            .catch((error) => {
                if (error?.response?.status === 404) {
                    setState("invalid-token");
                    return;
                }

                setState("error");
            });
    }, [props?.title, token]);

    function updatePreference(field, checked) {
        if (state === "saved") {
            setState("ready");
        }

        setValues((current) => ({
            ...current,
            [field]: checked
        }));
    }

    function savePreferences(event) {
        event.preventDefault();
        if (!token || (state !== "ready" && state !== "saved")) return;

        setIsSaving(true);
        axios.post(`/api/v2/publicusers/preferences?unsubscribe_token=${encodeURIComponent(token)}`, {
            optInEmail: values.optInEmail,
            optInSms: values.optInSms
        })
            .then(({data}) => {
                setValues((current) => ({
                    ...current,
                    optInEmail: data?.optInEmail ?? current.optInEmail,
                    optInSms: data?.optInSms ?? current.optInSms
                }));
                setState("saved");
            })
            .catch(() => {
                setState("error");
            })
            .finally(() => setIsSaving(false));
    }

    const canShowForm = state === "ready" || state === "saved";

    return (
        <Block {...props}>
            <div className={"flex items-center justify-center sm:pt-12"}>
                <div className={"rounded-theme section section-grey border w-full m-4 sm:max-w-lg sm:m-0"}>
                    <h2 className={"text-center !mb-6"}>{props?.title || "Manage Preferences"}</h2>

                    {state === "loading" && <Spinner containerClassName={"w-full mt-6"}/>}

                    {state === "missing-token" &&
                        <p className={"text-center leading-tight"}>
                            This unsubscribe link is missing a token. Please use the link from your email.
                        </p>}

                    {state === "invalid-token" &&
                        <p className={"text-center leading-tight"}>
                            This unsubscribe link is not valid anymore. Please use the latest campaign email link.
                        </p>}

                    {state === "error" &&
                        <p className={"text-center leading-tight text-red-500"}>
                            We could not load your preferences right now. Please try again later.
                        </p>}

                    {canShowForm &&
                        <form className={"flex flex-col gap-y-4"} onSubmit={savePreferences}>
                            {!!values.displayHint &&
                                <p className={"text-sm text-center leading-tight"}>
                                    Preferences for <span className={"font-semibold"}>{values.displayHint}</span>
                                </p>}
                            {!!values.emailHint &&
                                <p className={"text-sm text-center leading-tight text-gray-500"}>
                                    {values.emailHint}
                                </p>}

                            <label className={"flex items-start gap-x-3"}>
                                <input
                                    type="checkbox"
                                    checked={values.optInEmail}
                                    onChange={(event) => updatePreference("optInEmail", event.target.checked)}
                                />
                                <span>Receive campaign emails</span>
                            </label>

                            <label className={"flex items-start gap-x-3"}>
                                <input
                                    type="checkbox"
                                    checked={values.optInSms}
                                    onChange={(event) => updatePreference("optInSms", event.target.checked)}
                                />
                                <span>Receive campaign SMS updates</span>
                            </label>

                            {state === "saved" &&
                                <p className={"text-sm text-green-700"}>
                                    Your preferences have been updated.
                                </p>}

                            <button type="submit" disabled={isSaving} className={"button justify-center mt-2"}>
                                {isSaving ? "Saving..." : "Save Preferences"}
                            </button>
                        </form>}
                </div>
            </div>
        </Block>
    );
}
